import { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { RefObject, TouchEvent } from "react";

const MOBILE_BREAKPOINT = 1100;
const CARD_GAP = 3;
const SWIPE_THRESHOLD = 44;

const getItemsPerView = () =>
  typeof window !== "undefined" && window.innerWidth <= MOBILE_BREAKPOINT ? 1 : 3;

export type UseCarouselResult = {
  index: number;
  maxIndex: number;
  dotCount: number;
  overflowRef: RefObject<HTMLDivElement>;
  trackStyle: CSSProperties;
  cardStyle: CSSProperties | undefined;
  goPrev: () => void;
  goNext: () => void;
  goTo: (nextIndex: number) => void;
  onTouchStart: (event: TouchEvent<HTMLDivElement>) => void;
  onTouchEnd: (event: TouchEvent<HTMLDivElement>) => void;
};

export const useCarousel = (itemCount: number): UseCarouselResult => {
  const [index, setIndex] = useState(0);
  const [itemsPerView, setItemsPerView] = useState(getItemsPerView);
  const [itemWidth, setItemWidth] = useState(0);
  const overflowRef = useRef<HTMLDivElement>(null);
  const touchStartRef = useRef<number | null>(null);

  const maxIndex = useMemo(
    () => Math.max(0, itemCount - itemsPerView),
    [itemCount, itemsPerView]
  );

  const measure = useCallback(() => {
    const containerWidth = overflowRef.current?.offsetWidth ?? 0;
    if (containerWidth === 0) {
      return;
    }

    const totalGap = CARD_GAP * (itemsPerView - 1);
    setItemWidth((containerWidth - totalGap) / itemsPerView);
  }, [itemsPerView]);

  useEffect(() => {
    setIndex((previous) => Math.min(previous, maxIndex));
  }, [maxIndex]);

  useEffect(() => {
    const onResize = () => {
      setItemsPerView(getItemsPerView());
    };

    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    measure();

    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [measure]);

  const goPrev = () => setIndex((previous) => Math.max(0, previous - 1));
  const goNext = () => setIndex((previous) => Math.min(maxIndex, previous + 1));

  const goTo = (nextIndex: number) => {
    setIndex(Math.max(0, Math.min(nextIndex, maxIndex)));
  };

  const onTouchStart = (event: TouchEvent<HTMLDivElement>) => {
    touchStartRef.current = event.touches[0]?.clientX ?? null;
  };

  const onTouchEnd = (event: TouchEvent<HTMLDivElement>) => {
    if (touchStartRef.current === null) {
      return;
    }

    const distance = touchStartRef.current - (event.changedTouches[0]?.clientX ?? 0);

    if (Math.abs(distance) > SWIPE_THRESHOLD) {
      if (distance > 0) {
        goNext();
      } else {
        goPrev();
      }
    }

    touchStartRef.current = null;
  };

  return {
    index,
    maxIndex,
    dotCount: maxIndex + 1,
    overflowRef,
    trackStyle: {
      transform: `translateX(-${index * (itemWidth + CARD_GAP)}px)`,
    },
    cardStyle: itemWidth > 0 ? { flexBasis: `${itemWidth}px`, minWidth: `${itemWidth}px` } : undefined,
    goPrev,
    goNext,
    goTo,
    onTouchStart,
    onTouchEnd,
  };
};
